import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import { useFetch } from "97Parts/useFetch";
import {
  Backdrop,
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { memo, useEffect, useState } from "react";
import { LIST_HEADER_COLOR } from "Settings";

/**
 * 表示用ユーザー
 */
class DisplayUser {
  public sub: string;
  public accountType: string;
  public name: string;
  public email: string;
  public remarks: string;
  public userStatus: string;
  public createdAt: string;
  dateFormat(s: string) {
    return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`;
  }
  constructor(data: any) {
    this.sub = data?.sub;
    this.accountType = data?.AccountType;
    this.name = `${data?.family_name} ${data?.given_name}`;
    this.email = data?.email;
    this.remarks = data?.Remarks;
    this.userStatus = data?.UserStatus;
    this.createdAt = this.dateFormat(data?.CreatedAt);
  }
}

/**
 * テーブルヘッダ
 */
const Header = memo(() => {
  const ColorHeader = styled(TableCell)(({ theme }) => ({
    backgroundColor: LIST_HEADER_COLOR,
    color: theme.palette.common.white,
  }));
  return (
    <TableRow>
      <ColorHeader align="center">権限</ColorHeader>
      <ColorHeader align="center">利用者</ColorHeader>
      <ColorHeader align="center">メールアドレス</ColorHeader>
      <ColorHeader align="center">作成日</ColorHeader>
      <ColorHeader align="center">備考</ColorHeader>
    </TableRow>
  );
});

/**
 * アカウント一覧ページ
 * @returns
 */
export default function AllAccounts() {
  const rowPerPages = [10, 25, 100];
  const [users, setUsers] = useState<Array<DisplayUser>>([]);
  const [rowsPerPage, setRowsPerPage] = useState(rowPerPages[0]);
  const [page, setPage] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const temp = (process.env.REACT_APP_URL_USERS || "").split("/");
  const url = temp.slice(0, temp.length - 1).join("/");
  const { get } = useFetch(`${url}/all_users`);

  useEffect(() => {
    setPageLoading(true);
    const f = async () => {
      const data = await get<Array<any>>();
      const l: Array<DisplayUser> = [];
      if (data) {
        data.forEach((d) => {
          const u = new DisplayUser(d);
          l.push(u);
        });
      }
      setUsers(l);
    };
    try {
      f();
    } finally {
      setPageLoading(false);
    }
  }, [get]);

  const accountTypes = ["administrtaor", "sub_admin", "manager", "user"];
  const rows = users
    ? users
        .sort((a, b) => {
          // アカウント種別 > 姓名 順
          if (a.accountType === b.accountType) {
            return a.name.localeCompare(b.name);
          } else {
            return accountTypes.indexOf(a.accountType) >
              accountTypes.indexOf(b.accountType)
              ? 1
              : -1;
          }
        })
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((user) => (
          <TableRow key={user.sub}>
            <TableCell align="center">{user.accountType}</TableCell>
            <TableCell align="center">{user.name}</TableCell>
            <TableCell align="center">{user.email}</TableCell>
            <TableCell align="center">{user.createdAt}</TableCell>
            <TableCell align="center">{user.remarks}</TableCell>
          </TableRow>
        ))
    : null;

  /**
   * ページ移動イベントハンドラ
   * @param event
   * @param newPage 移動先ページ
   */
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  /**
   * １ページあたり行数変更イベントハンドラ
   * @param event
   */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <PageTemplate title="アカウント一覧">
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("アカウント一覧"),
        ]}
      />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "50vh" }}>
          <Table stickyHeader aria-label="sticky table" size="small">
            <TableHead style={{ whiteSpace: "nowrap" }}>
              <Header />
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowPerPages}
          component="div"
          count={users?.length ?? 0}
          rowsPerPage={rowsPerPage}
          page={users ? page : 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Backdrop open={pageLoading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </PageTemplate>
  );
}
